import { useEffect, useMemo, useState } from 'react';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowLeft2, ArrowRight2, ArrowUp, CloseCircle, InfoCircle, Refresh, TickCircle, Trash } from 'iconsax-reactjs';
import {
  FIB_TIMEFRAMES,
  FIB_VISIBLE_BARS,
  TIMEFRAME_LABEL,
  fibLevels,
  retracementOf,
  swingDirection,
  swingRange,
  timeframeShort,
  type AutoSwing,
  type TeachingFib,
  type Timeframe,
} from '@pipyar/core';
import { useTheme } from '@/lib/useTheme';
import { useSymbol } from '@/lib/useSymbol';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { FibChart, type ChartPoint } from '@/components/chart/FibChart';
import { ChartFrame } from '@/components/chart/ChartFrame';
import { chartPalette } from '@/components/chart/chartTheme';
import type { FibDrawing } from '@/components/chart/fibPrimitive';
import { FibTabs } from './FibTabs';
import {
  useAutoSwings,
  useFibCandles,
  useTeachingFibMutations,
  useTeachingFibs,
  useVerdictMutations,
} from './api';

/** Stored (broker-clock) time as dd/mm hh:mm, read as UTC like the chart axis. */
function formatStored(time: number): string {
  const d = new Date(time * 1000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}

/**
 * The Fibonacci module.
 *
 * One chart per timeframe, stacked M1 → D1. Each carries two kinds of draw:
 *
 * - the owner's TEACHING draws made on that timeframe with the draw tool,
 *   kept with a note and never removed automatically — worked examples of
 *   which swing the rules mean;
 * - my swing for that timeframe, picked by the rules as taught so far and
 *   removed once price passes its start or end.
 *
 * Separate charts rather than one combined (tried both): the owner reads
 * each timeframe's draw on its own candles. The draw tool snaps every click
 * to the nearer wick of the bar under the pointer.
 */
export function FibonacciPage() {
  const { theme } = useTheme();
  const { symbol } = useSymbol();
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const fetching = useIsFetching({ queryKey: ['fib-candles'] }) > 0;
  const palette = chartPalette(theme);

  const [showMine, setShowMine] = useState(true);
  const [showYours, setShowYours] = useState(true);
  const [showPivots, setShowPivots] = useState(true);

  const auto = useAutoSwings(symbol ?? null);
  const teaching = useTeachingFibs(symbol ?? null);
  const mutations = useTeachingFibMutations();

  const error = [teaching, auto].find((q) => q.error)?.error;
  const digits = 2;

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('fib.title')}</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {t('fib.subtitle', { symbol: symbol ?? '—' })}
          </p>
        </div>
        <FibTabs />
        <span className="ms-auto flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <Toggle checked={showYours} onChange={setShowYours} label={t('fib.showYours')} swatch={palette.fibTeaching} />
          <Toggle checked={showMine} onChange={setShowMine} label={t('fib.showMine')} />
          <Toggle checked={showPivots} onChange={setShowPivots} label={t('fib.showPivots')} />
        </span>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('common.refresh')}
          onClick={() => {
            void queryClient.invalidateQueries({ queryKey: ['fib-candles'] });
            void queryClient.invalidateQueries({ queryKey: ['fib-teaching'] });
            void queryClient.invalidateQueries({ queryKey: ['fib-auto'] });
          }}
        >
          <Refresh size={18} className={fetching ? 'animate-spin' : undefined} />
        </Button>
      </header>

      {error && (
        <div
          role="alert"
          className="flex gap-2.5 rounded-lg border border-danger/30 bg-danger/8 p-3 text-sm text-danger"
        >
          <InfoCircle size={18} variant="Bold" className="mt-px shrink-0" />
          <span>{error instanceof Error ? error.message : t('fib.error')}</span>
        </div>
      )}

      {FIB_TIMEFRAMES.map((tf) => (
        <FibChartSection
          key={tf}
          symbol={symbol ?? null}
          timeframe={tf}
          autos={auto.data?.swings[tf] ?? []}
          autoPending={auto.isPending}
          teaching={(teaching.data?.fibs ?? []).filter((f) => f.timeframe === tf)}
          showMine={showMine}
          showYours={showYours}
          showPivots={showPivots}
          onCreate={(start, end) =>
            symbol && mutations.create.mutate({ symbol, timeframe: tf, start, end, note: '' })
          }
        />
      ))}

      <section className="glass rounded-2xl p-3">
        <h2 className="text-sm font-medium">{t('fib.yourDraws')}</h2>
        <p className="mb-2 text-[10px] leading-snug text-muted-foreground">{t('fib.yourDrawsNote')}</p>
        {teaching.isPending ? (
          <p className="text-xs text-muted-foreground">{t('common.loading')}</p>
        ) : (teaching.data?.fibs.length ?? 0) === 0 ? (
          <p className="text-xs text-muted-foreground">{t('fib.noDraws')}</p>
        ) : (
          <ul className="space-y-2">
            {teaching.data!.fibs.map((f) => (
              <TeachingRow
                key={f.id}
                fib={f}
                digits={digits}
                onNote={(note) => mutations.update.mutate({ id: f.id, patch: { note } })}
                onDelete={() => mutations.remove.mutate(f.id)}
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/** One timeframe's chart: its candles, my swing, the owner's draws on it, and the draw tool. */
function FibChartSection({
  symbol,
  timeframe,
  autos,
  autoPending,
  teaching,
  showMine,
  showYours,
  showPivots,
  onCreate,
}: {
  symbol: string | null;
  timeframe: Timeframe;
  autos: AutoSwing[];
  autoPending: boolean;
  teaching: TeachingFib[];
  showMine: boolean;
  showYours: boolean;
  showPivots: boolean;
  onCreate: (start: ChartPoint, end: ChartPoint) => void;
}) {
  const { theme } = useTheme();
  const { t } = useI18n();
  const palette = chartPalette(theme);
  const color = palette.fib[timeframe] ?? palette.text;

  const query = useFibCandles(symbol, timeframe);
  const data = query.data;
  const digits = data?.digits ?? 2;

  // The forming bar is appended so the chart's last candle is live; useLiveBar
  // then moves it tick by tick.
  const candles = useMemo(() => {
    const closed = data?.candles ?? [];
    return data?.live ? [...closed, data.live] : closed;
  }, [data]);

  // The detector's swings, one per scale; removal was judged by the API on the same bars.
  const live = autos.filter((s) => !s.invalid);
  const lastClose = candles.length > 0 ? candles[candles.length - 1]!.close : null;

  // ---- draw mode ----
  const [drawing, setDrawing] = useState(false);
  const [pendingStart, setPendingStart] = useState<ChartPoint | null>(null);
  const [hover, setHover] = useState<ChartPoint | null>(null);

  const stopDrawing = () => {
    setDrawing(false);
    setPendingStart(null);
    setHover(null);
  };

  const onPick = (point: ChartPoint) => {
    if (!pendingStart) {
      setPendingStart(point);
      return;
    }
    if (point.time === pendingStart.time && point.price === pendingStart.price) return;
    onCreate(pendingStart, point);
    setPendingStart(null);
    setHover(null);
  };

  useEffect(() => {
    if (!drawing) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') stopDrawing();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawing]);

  /**
   * One drawing per slide. The detector's swings first (smallest scale
   * first), then the owner's draws on this timeframe. Several on one chart hid each other, so the chart shows one
   * at a time and the arrows step through them.
   */
  type Slide = { key: string; drawing: FibDrawing; auto?: AutoSwing; teaching?: TeachingFib };
  const slides = useMemo<Slide[]>(() => {
    const out: Slide[] = [];
    if (showMine) {
      for (const s of live) {
        out.push({
          key: `auto-${s.scale}`,
          auto: s,
          drawing: {
            tag: scaleTag(timeframe, s.scale),
            color,
            direction: s.direction,
            start: s.start,
            end: s.end,
            levels: s.levels,
            digits,
            path: showPivots ? s.pivots.map((p) => ({ time: p.time, price: p.price })) : undefined,
          },
        });
      }
    }
    if (showYours) {
      for (const f of teaching) {
        out.push({
          key: `t-${f.id}`,
          teaching: f,
          drawing: {
            tag: `T${f.id}`,
            color: palette.fibTeaching,
            direction: f.direction,
            start: f.start,
            end: f.end,
            levels: fibLevels(f),
            digits,
          },
        });
      }
    }
    return out;
  }, [showMine, showYours, showPivots, live, teaching, timeframe, color, palette, digits]);

  // The slide in view, remembered by key so a refetch that reorders or adds
  // swings does not jump the chart to a different drawing.
  const [slideKey, setSlideKey] = useState<string | null>(null);
  const index = Math.max(0, slides.findIndex((x) => x.key === slideKey));
  const current = slides[index] ?? null;
  const step = (d: number) => {
    if (slides.length === 0) return;
    const next = (index + d + slides.length) % slides.length;
    setSlideKey(slides[next]!.key);
  };

  const drawings = useMemo<FibDrawing[]>(() => {
    const out: FibDrawing[] = current ? [current.drawing] : [];
    if (pendingStart && hover && hover.time !== pendingStart.time) {
      const s = { direction: swingDirection(pendingStart, hover), start: pendingStart, end: hover };
      out.push({ tag: 'new', color: palette.fibTeaching, ...s, levels: fibLevels(s), digits });
    }
    return out;
  }, [current, pendingStart, hover, palette, digits]);

  const slideNav = (compact: boolean) =>
    slides.length > 1 ? (
      <span className="flex items-center gap-0.5">
        <button
          type="button"
          onClick={() => step(-1)}
          aria-label={t('fib.prevDraw')}
          className={cn('rounded-lg bg-elevated text-muted-foreground hover:text-foreground', compact ? 'p-1' : 'p-1.5')}
        >
          <ArrowLeft2 size={14} />
        </button>
        <span className={cn('tabular min-w-12 text-center text-muted-foreground', compact ? 'text-xs' : 'text-xs')}>
          {index + 1} / {slides.length}
        </span>
        <button
          type="button"
          onClick={() => step(1)}
          aria-label={t('fib.nextDraw')}
          className={cn('rounded-lg bg-elevated text-muted-foreground hover:text-foreground', compact ? 'p-1' : 'p-1.5')}
        >
          <ArrowRight2 size={14} />
        </button>
        {current && <span className="ms-1 text-xs font-medium">{current.drawing.tag}</span>}
      </span>
    ) : null;

  const drawButton = (compact: boolean) => (
    <>
      <button
        type="button"
        onClick={() => (drawing ? stopDrawing() : setDrawing(true))}
        aria-pressed={drawing}
        className={cn(
          'rounded-lg font-medium transition-colors',
          compact ? 'px-2 py-1 text-xs' : 'px-2.5 py-1 text-xs',
          drawing ? 'bg-primary text-primary-foreground' : 'bg-elevated text-muted-foreground hover:text-foreground',
        )}
      >
        {drawing ? t('fib.cancel') : t('fib.draw')}
      </button>
      {drawing && (
        <span className="text-xs text-muted-foreground">
          {pendingStart ? t('fib.drawEnd') : t('fib.drawStart')}
        </span>
      )}
    </>
  );

  const label = `${timeframeShort(timeframe)} · ${TIMEFRAME_LABEL[timeframe]}`;

  return (
    // min-w-0: see KnotsPage — without it the section cannot shrink back after
    // a full-screen pass and the page scrolls sideways.
    <section className="min-w-0 space-y-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <span className="inline-block h-0.5 w-3" style={{ backgroundColor: color }} aria-hidden="true" />
        <h2 className="text-sm font-semibold">{timeframeShort(timeframe)}</h2>
        <span className="text-muted-foreground">{TIMEFRAME_LABEL[timeframe]}</span>

        {autos.length === 0 && !autoPending && <span className="text-muted-foreground">{t('fib.autoNone')}</span>}
        <span className="ms-auto flex items-center gap-2">
          {slideNav(false)}
          {drawButton(false)}
          <span className="text-muted-foreground tabular">
            {data ? t('fib.barsLoaded', { count: data.count }) : ''}
          </span>
        </span>
      </div>

      {query.error && (
        <div
          role="alert"
          className="flex gap-2.5 rounded-lg border border-danger/30 bg-danger/8 p-3 text-sm text-danger"
        >
          <InfoCircle size={18} variant="Bold" className="mt-px shrink-0" />
          <span>{query.error instanceof Error ? query.error.message : t('fib.error')}</span>
        </div>
      )}

      <ChartFrame
        label={label}
        fill={false}
        fullscreenToolbar={
          <>
            {slideNav(true)}
            {slides.length > 1 && <span className="mx-1 h-4 w-px bg-border" aria-hidden="true" />}
            {drawButton(true)}
          </>
        }
        className="group/frame h-[30rem] overflow-hidden glass rounded-2xl"
      >
        {query.isPending ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            {t('common.loading')}
          </div>
        ) : candles.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            {t('fib.noCandles', { timeframe: timeframeShort(timeframe) })}
          </div>
        ) : (
          <FibChart
            className="h-full w-full"
            candles={candles}
            drawings={drawings}
            visibleBars={FIB_VISIBLE_BARS[timeframe]}
            digits={digits}
            theme={theme}
            resetKey={`${symbol}-${timeframe}`}
            drawMode={drawing}
            onPick={onPick}
            onHover={setHover}
          />
        )}
      </ChartFrame>

      {current?.auto && (
        <ul className="text-xs">
          <AutoRow swing={current.auto} timeframe={timeframe} digits={digits} lastClose={lastClose} symbol={symbol} />
        </ul>
      )}
      {current?.teaching && (
        <p className="text-xs leading-snug text-muted-foreground">
          <span className="font-medium">T{current.teaching.id}:</span>{' '}
          {current.teaching.note || t('fib.noNote')}
        </p>
      )}
      {autos.some((s) => s.invalid) && (
        <p className="text-[11px] text-muted-foreground">
          {t('fib.removedCount', { n: autos.filter((s) => s.invalid).length })}
        </p>
      )}

    </section>
  );
}

/** `H4` for the base scale, `H4 ×3` for the others. */
function scaleTag(tf: Timeframe, scale: number): string {
  return scale === 1 ? timeframeShort(tf) : `${timeframeShort(tf)} ×${scale}`;
}

function AutoRow({
  swing,
  timeframe,
  digits,
  lastClose,
  symbol,
}: {
  swing: AutoSwing;
  timeframe: Timeframe;
  digits: number;
  lastClose: number | null;
  symbol: string | null;
}) {
  const { t } = useI18n();
  const retracement = !swing.invalid && lastClose !== null ? retracementOf(swing, lastClose) : null;
  const verdicts = useVerdictMutations();
  const current = swing.verdict ?? null;
  const [note, setNote] = useState(current?.note ?? '');
  useEffect(() => setNote(current?.note ?? ''), [current?.note]);

  const judge = (verdict: 'approve' | 'deny') => {
    if (!symbol) return;
    // Pressing the same verdict again clears it.
    if (current?.verdict === verdict) {
      verdicts.clear.mutate(current.id);
      return;
    }
    verdicts.set.mutate({
      symbol,
      timeframe,
      scale: swing.scale,
      direction: swing.direction,
      start: { time: swing.start.time, price: swing.start.price, extreme: swing.start.extreme },
      end: { time: swing.end.time, price: swing.end.price, extreme: swing.end.extreme },
      running: swing.running,
      reversalPct: swing.reversalPct,
      verdict,
      note,
    });
  };

  return (
    <li className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => judge('approve')}
          aria-pressed={current?.verdict === 'approve'}
          title={t('fib.approve')}
          className={cn(
            'flex items-center gap-1 rounded-lg px-2 py-1 font-medium transition-colors',
            current?.verdict === 'approve' ? 'bg-bull text-primary-foreground' : 'bg-elevated text-muted-foreground hover:text-bull',
          )}
        >
          <TickCircle size={14} variant={current?.verdict === 'approve' ? 'Bold' : 'Linear'} />
          {t('fib.approve')}
        </button>
        <button
          type="button"
          onClick={() => judge('deny')}
          aria-pressed={current?.verdict === 'deny'}
          title={t('fib.deny')}
          className={cn(
            'flex items-center gap-1 rounded-lg px-2 py-1 font-medium transition-colors',
            current?.verdict === 'deny' ? 'bg-bear text-primary-foreground' : 'bg-elevated text-muted-foreground hover:text-bear',
          )}
        >
          <CloseCircle size={14} variant={current?.verdict === 'deny' ? 'Bold' : 'Linear'} />
          {t('fib.deny')}
        </button>
      </span>
      <span className="w-14 shrink-0 font-semibold">{scaleTag(timeframe, swing.scale)}</span>
      {swing.invalid ? (
        <span className="text-muted-foreground">
          {t(swing.invalid.passed === 'start' ? 'fib.removedStart' : 'fib.removedEnd', {
            price: swing.invalid.price.toFixed(digits),
            time: formatStored(swing.invalid.time),
          })}
        </span>
      ) : (
        <>
          <span
            className={cn(
              'flex items-center gap-0.5 rounded px-1.5 py-0.5 font-medium',
              swing.direction === 'up' ? 'bg-bull/15 text-bull' : 'bg-bear/15 text-bear',
            )}
          >
            {swing.direction === 'up' ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
            {t(swing.direction === 'up' ? 'fib.swingUp' : 'fib.swingDown')}
          </span>
          <span className="tabular text-muted-foreground">
            {swing.start.price.toFixed(digits)} → {swing.end.price.toFixed(digits)} ({swingRange(swing).toFixed(digits)})
          </span>
          {swing.running && <span className="rounded bg-elevated px-1.5 py-0.5 text-muted-foreground">{t('fib.running')}</span>}
          {retracement !== null && (
            <span className="tabular">
              {t('fib.retraced')}{' '}
              <span className={cn('font-semibold', retracement > 1 ? 'text-bear' : retracement < 0 ? 'text-bull' : undefined)}>
                {Math.round(retracement * 1000) / 10}%
              </span>
            </span>
          )}
          <span className="text-muted-foreground">{t('fib.reversal', { pct: (swing.reversalPct * 100).toFixed(1) })}</span>
          {(swing.start.refinedOn || swing.end.refinedOn) && (
            <span className="text-muted-foreground">
              {t('fib.refinedOn', {
                tfs: [swing.start.refinedOn, swing.end.refinedOn].filter(Boolean).map((x) => timeframeShort(x!)).join('/'),
              })}
            </span>
          )}
          {swing.score && (
            <span className="rounded bg-elevated px-1.5 py-0.5 text-muted-foreground">{t('fib.score', { hits: swing.score.hits })}</span>
          )}
        </>
      )}
      {current && (
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => {
            if (note !== current.note) verdicts.update.mutate({ id: current.id, patch: { note } });
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
          placeholder={t('fib.verdictNotePlaceholder')}
          className="basis-full rounded-md border border-input bg-background px-2 py-1 text-xs outline-none placeholder:text-muted-foreground/60 focus-visible:border-ring"
        />
      )}
    </li>
  );
}

function TeachingRow({
  fib,
  digits,
  onNote,
  onDelete,
}: {
  fib: TeachingFib;
  digits: number;
  onNote: (note: string) => void;
  onDelete: () => void;
}) {
  const { t } = useI18n();
  const [note, setNote] = useState(fib.note);
  useEffect(() => setNote(fib.note), [fib.note]);

  return (
    <li className="space-y-1 rounded-lg bg-elevated/60 p-2 text-xs">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-semibold">T{fib.id}</span>
        <span className="rounded bg-muted px-1.5 py-0.5">{timeframeShort(fib.timeframe)}</span>
        <span
          className={cn(
            'flex items-center gap-0.5 rounded px-1.5 py-0.5 font-medium',
            fib.direction === 'up' ? 'bg-bull/15 text-bull' : 'bg-bear/15 text-bear',
          )}
        >
          {fib.direction === 'up' ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
          {t(fib.direction === 'up' ? 'fib.swingUp' : 'fib.swingDown')}
        </span>
        <span className="tabular text-muted-foreground">
          {formatStored(fib.start.time)} {fib.start.price.toFixed(digits)} → {formatStored(fib.end.time)}{' '}
          {fib.end.price.toFixed(digits)} ({swingRange(fib).toFixed(digits)})
        </span>
        <button
          type="button"
          onClick={onDelete}
          aria-label={t('common.delete')}
          className="ms-auto rounded p-1 text-muted-foreground hover:text-danger"
        >
          <Trash size={14} />
        </button>
      </div>
      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onBlur={() => {
          if (note !== fib.note) onNote(note);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
        placeholder={t('fib.notePlaceholder')}
        className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs outline-none placeholder:text-muted-foreground/60 focus-visible:border-ring"
      />
    </li>
  );
}

function Toggle({
  checked,
  onChange,
  label,
  swatch,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  swatch?: string;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-1.5 select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="size-3.5 rounded border-border accent-[var(--primary)]"
      />
      {swatch && <span className="inline-block h-0.5 w-3" style={{ backgroundColor: swatch }} aria-hidden="true" />}
      {label}
    </label>
  );
}
